import axios, { type InternalAxiosRequestConfig } from "axios";
import { expireSession, getAuthHeaders } from "../context/authStorage";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? "https://api.staging.ravenapp.dev/near_miss";

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

type SessionRequest = InternalAxiosRequestConfig & { sessionJWT?: string };
const publicPaths = new Set([
  "/auth/password_login", "/auth/register", "/auth/get_auth_methods",
  "/auth/get_user_by_identifier", "/auth/discovery/magic-link/send",
  "/auth/discovery/magic-link/authenticate", "/auth/discovery/oauth/authenticate",
  "/ess/auth/check_user", "/ess/auth/register", "/ess/auth/roles", "/departments",
]);

export function isProtectedApiRequest(url: string): boolean {
  const base = new URL(API_BASE_URL, window.location.origin);
  const target = new URL(url, window.location.origin);
  const prefix = base.pathname.replace(/\/$/, "");
  if (base.origin !== target.origin || !target.pathname.startsWith(`${prefix}/`)) return false;
  return !publicPaths.has(target.pathname.slice(prefix.length).replace(/\/$/, ""));
}

api.interceptors.request.use((config: SessionRequest) => {
  const protectedRequest = isProtectedApiRequest(api.getUri(config));
  for (const key of ["x-session-jwt", "x-user-id", "x-account-id", "x-application"]) {
    config.headers.delete(key);
  }
  config.sessionJWT = undefined;
  if (protectedRequest) {
    const headers = getAuthHeaders();
    Object.entries(headers).forEach(([key, value]) => config.headers.set(key, value));
    config.sessionJWT = headers["x-session-jwt"];
  }
  // Public ESS registration and authenticated role lookups share this scope.
  const target = new URL(api.getUri(config), window.location.origin);
  const base = new URL(API_BASE_URL, window.location.origin);
  if (target.origin === base.origin && target.pathname.startsWith(`${base.pathname.replace(/\/$/, "")}/`)) {
    config.headers.set("x-application", "ess");
  }
  return config;
});

api.interceptors.response.use((response) => response, (error) => {
  if (error.response?.status === 401) expireSession(error.config?.sessionJWT);
  return Promise.reject(error);
});

export async function revokeSession(token: string): Promise<void> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000);
  try {
    await fetch(`${API_BASE_URL.replace(/\/$/, "")}/auth/logout`, {
      method: "POST", headers: { "x-session-jwt": token }, signal: controller.signal,
    });
  } finally {
    clearTimeout(timeout);
  }
}

export async function verifyHandoffSession(token: string, userId: string, accountId: string): Promise<void> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch(`${API_BASE_URL.replace(/\/$/, "")}/auth/account`, {
      headers: { "x-session-jwt": token, "x-user-id": userId, "x-account-id": accountId },
      signal: controller.signal,
    });
    if (!response.ok || (await response.json()).id !== accountId) {
      throw new Error("Authenticated session does not match the requested account.");
    }
  } finally {
    clearTimeout(timeout);
  }
}
